import { Card, Collapse } from "antd"
import ChecklistHeader from "./ChecklistHeader"
import { useReducer, useState } from "react"
import ChecklistContext from "./ChecklistContext"
import { Action, ChecklistItemData, createNewChecklist, getSectionCompletionEventData, reducer } from "./checklistModel"
import sectionsList from "./sectionsList"
import SectionContent from "./SectionContent"
import SectionHeader from "./SectionHeader"
import VSpace from "../common/VSpace"
import { usePrimaryGoalCompletedDateMutation, usePrimaryGoalMutation, useSuccessChecklist, useSuccessChecklistMutation } from "./checklistQueries"
import { useSavedState } from "../../hooks/useSavedState"
import { useDebouncedCallback } from "../../helpers/debounce"

export type Props = {
  onSectionChange?: (data: any) => void
}

const SuccessChecklist = ({ onSectionChange }: Props) => {
  const [lastUniqueSectionChange, setLastUniqueSectionChange] = useState('')
  const saveMutation = useSuccessChecklistMutation()
  const primaryGoalMutation = usePrimaryGoalMutation()
  const primaryGoalCompletedMutation = usePrimaryGoalCompletedDateMutation()
  const debouncedSave = useDebouncedCallback((steps: ChecklistItemData[]) => saveMutation.mutate(steps), 3000)
  const savingReducer = (state: ChecklistItemData[], action: Action) => {
    const updated = reducer(state, action)
    if (action.type !== 'CHECKLIST_LOADED') {
      debouncedSave(updated)

      const data = getSectionCompletionEventData(updated)
      if (data.uniqueString !== lastUniqueSectionChange && onSectionChange) {
        setLastUniqueSectionChange(data.uniqueString)
        onSectionChange(data)
      }
    }

    return updated
  }

  const [showChecklist, setShowChecklist] = useState(false)
  const [checklistPrimaryGoal, setChecklistPrimaryGoal] = useState<string | null>(null)
  const [checklistPrimaryGoalDate, setChecklistPrimaryGoalDate] = useState<string | null>(null)
  const [checklistPrimaryGoalCompletedDate, setChecklistPrimaryGoalCompletedDate] = useState<string | null>(null)
  const [data, dispatch] = useReducer(savingReducer, createNewChecklist())
  const [isOpen, setIsOpen] = useSavedState('success-checklist:is-open', true)
  const [selectedSection, setSelectedSection] = useState('')
  useSuccessChecklist(result => {
    setShowChecklist(result.showChecklist)
    setChecklistPrimaryGoal(result.checklistPrimaryGoal)
    setChecklistPrimaryGoalDate(result.checklistPrimaryGoalDate)
    setChecklistPrimaryGoalCompletedDate(result.checklistPrimaryGoalCompletedDate)
    dispatch({ type: 'CHECKLIST_LOADED', steps: result.steps })
  })



  const handleSectionChange = (section: string) => {
    setSelectedSection(section === selectedSection ? '' : section)
  }

  const handlePrimaryGoalChange = (section: string) => {
    setChecklistPrimaryGoal(section)
    primaryGoalMutation.mutate({ checklistPrimaryGoal: section, checklistPrimaryGoalDate }) 
  }

  const handlePrimaryGoalDateChange = (date: string | null) => {
    setChecklistPrimaryGoalDate(date)
    primaryGoalMutation.mutate({ checklistPrimaryGoal, checklistPrimaryGoalDate: date }) 
  }

  const handlePrimaryGoalCompletedDateChange = (date: string | null) => {
    setChecklistPrimaryGoalDate(date)
    primaryGoalCompletedMutation.mutate({ checklistPrimaryGoal, checklistPrimaryGoalCompletedDate: date }) 
  }

  if (!showChecklist) {
    return <></>
  }

  return <ChecklistContext.Provider value={{
    data,
    dispatch
  }}>
    <div style={{ width: '800px', margin: 'auto' }}>
      <Collapse ghost collapsible="icon" activeKey={isOpen ? 'main' : ''} items={[
        {
          key: 'main',
          label: <ChecklistHeader isShowing={isOpen} onIsShowingChange={setIsOpen} />,
          showArrow: false,
          children: <VSpace>
            {sectionsList.map(section => <Card key={section.section} styles={{ body: { padding: 0 } }}>
              <Collapse
                ghost
                onChange={() => handleSectionChange(section.section)} activeKey={selectedSection === section.section ? [section.section] : []}
                items={[
                  {
                    key: section.section,
                    label: <SectionHeader 
                              section={section} 
                              primaryGoal={checklistPrimaryGoal}
                              primaryGoalDate={checklistPrimaryGoalDate ? checklistPrimaryGoalDate : null}
                              primaryGoalCompletedDate={checklistPrimaryGoalCompletedDate ? checklistPrimaryGoalCompletedDate : null} 
                              onPrimaryGoalChange={handlePrimaryGoalChange}
                              onPrimaryGoalDateChange={handlePrimaryGoalDateChange}
                              onPrimaryGoalCompletedDateChange={handlePrimaryGoalCompletedDateChange} />,
                    showArrow: false,
                    children: <SectionContent section={section.section} steps={section.steps} />
                  }
                ]}
              />
            </Card>)}
          </VSpace>
        }
      ]} />
    </div>
  </ChecklistContext.Provider>
}

export default SuccessChecklist
